import { useTranslation } from 'react-i18next';
import { type DehydratedState } from '@tanstack/react-query';

import { type DeviceType } from '@cx-modules/types';

import { OffersCarousel, OffersGrouped, OffersSlim } from '~/modules';
import { getOffersClickoutServiceProps } from '~/modules/offers-clickout';
import styles from '~/routes/$/components/Widgets/Offers/Offers.module.css';

export interface GridGroupOffersProps {
  DISABLE_OFFER_WIDGETS?: boolean;
  apiKey: string;
  autohideTerms?: string;
  className: string;
  countryCode?: string;
  currentPathWithRepub: string;
  dehydratedState: DehydratedState | undefined;
  deviceType: DeviceType;
  disableAnalytics?: boolean;
  fullPageUrl: string;
  geoTargeting?: boolean;
  geolocation?: string;
  gtmId: string;
  inheritFontFamily?: boolean;
  offerLimit?: string;
  repub: any;
  sortBy?: string;
  subdivisionCode?: string;
  theme?: any;
  variation: string;
  vcId: string;
  visibleOffersCount?: string;
}

type CleanedProps = Omit<GridGroupOffersProps, 'DISABLE_OFFER_WIDGETS'> &
  ReturnType<typeof getOffersClickoutServiceProps>;

const isClient = typeof window !== 'undefined';
const hasValidDataLayer = (props: any) => isClient && !props.disableAnalytics && window.dataLayer?.length;

const cleanProps = (props: Omit<GridGroupOffersProps, 'DISABLE_OFFER_WIDGETS'>): CleanedProps => {
  const commonProps = {
    // NGF-6268: the offers modules no longer read clickout props from provider context, so this
    // renderer must inject the full set (mirrors the page-template componentMap). Sourced from the
    // GenericConfig values GridGroup passes down + the global ENV.
    ...getOffersClickoutServiceProps({
      clickoutUrl: ENV.CLICKOUT_URL,
      currentPathWithRepub: props.currentPathWithRepub,
      deviceType: props.deviceType,
      fullPageUrl: props.fullPageUrl,
      gtmId: props.gtmId,
    }),
    analyticsDataLayer: hasValidDataLayer(props) ? window.dataLayer : undefined,
  };

  if (props.variation === 'grouped' && !props.geolocation) {
    const geolocation = `${props.countryCode?.toLowerCase()}-${props.subdivisionCode?.toLowerCase()}`;
    if (geolocation.length > 1) {
      props.geolocation = geolocation;
    }
  }

  // If geotargeting is not enabled, remove the countryCode and subdivisionCode.
  if (!props.geoTargeting) {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { countryCode, subdivisionCode, ...cleanedProps } = props;

    return {
      ...cleanedProps,
      ...commonProps,
    };
  }

  return {
    ...props,
    ...commonProps,
  };
};

export function GridGroupOffers(dirtyProps: GridGroupOffersProps) {
  const { DISABLE_OFFER_WIDGETS, ...restProps } = dirtyProps;
  const { i18n } = useTranslation();

  if (DISABLE_OFFER_WIDGETS) {
    return null;
  }

  const props = cleanProps(restProps);

  const renderOffersWidget = () => {
    switch (props.variation) {
      case 'grouped':
        if (!props.geolocation) {
          console.warn('Geolocation is required for grouped offers widget');
          return null;
        }
        return <OffersGrouped {...props} geolocation={props.geolocation} i18n={i18n} />;
      case 'slim':
        return <OffersSlim {...props} i18n={i18n} />;
      case 'carousel':
      default:
        return <OffersCarousel {...props} i18n={i18n} />;
    }
  };

  return (
    <div className={styles[props.className]} data-testid="offers-widget-div">
      {renderOffersWidget()}
    </div>
  );
}
